import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useDrag } from '@use-gesture/react';
import { motion } from 'framer-motion';
import { bookAnalytics } from '@/services/bookAnalytics';
import { Book } from '@/utils/bookRouting';
import { formatTime, getDisplayedPlaybackTime } from '@/utils/timeUtils';
import AlephEntry, { type ConversationMode } from './AlephEntry';
import PlayerControls from './PlayerControls';
import ReadPlayer from './ReadPlayer';
import { useTheme } from '@/contexts/ThemeContext';
import ProgressBar from './ProgressBar';
import ChapterSelectionIcon from './icons/ChapterSelectionIcon';
import ClockIcon from './icons/ClockIcon';
import ChapterList from './ChapterList';
import { PipecatClient } from '@pipecat-ai/client-js';
import { useBreakpoint } from "@/hooks/useBreakpoint";
import usePlayerKeyboardShortcuts from '@/hooks/usePlayerKeyboardShortcuts';
import useClickOutside from '@/hooks/useClickOutside';
import { ReaderBook, ReaderChapter } from '@/services/booksApi';
import SleepTimerMenu, { getSleepTimerButtonLabel } from './SleepTimerMenu';
import PlaybackSpeedControl from './PlaybackSpeedControl';
import { SleepTimerState } from '@/hooks/useSleepTimer';
import type {
  AnalyticsInputMethod,
  InteractionContext,
  VoiceEndContext,
  VoiceStartContext,
} from '@/services/analyticsContext';
import { ChapterTimingsStatus } from '@/hooks/useChapterAudio';
import ControlPopupPresence from './ControlPopupPresence';
import { ChapterRuntime } from '@/utils/ordinalSync';
import type { ChatSessionSummary } from '@/types/chat';
import type {
  ReaderContentReadiness,
  ReaderChapterSelectionRequest,
  ReaderPositionAcknowledgement,
  ReaderPositionRequest,
} from '@/types/readerReadiness';

type ButtonSize = "base" | "sm" | "md" | "lg" | "xl";

interface PersistentAudioPlayerProps {
  book: Book;
  audioRef: React.RefObject<HTMLAudioElement | null>;
  isPlaying: boolean;
  isReady: boolean;
  currentTime: number;
  duration: number;
  togglePlay: () => void;
  skipForward: () => void;
  skipBackward: () => void;
  onConnect: (context: VoiceStartContext) => Promise<boolean>;
  onDisconnect: (context: VoiceEndContext) => Promise<void>;
  isConnected: boolean;
  connectionStatus: string;
  isBuffering: boolean;
  onSeek: (time: number) => void;
  currentChapter: ReaderChapter | null;
  chapters: ReaderChapter[];
  onChapterSelect: (chapterId: string, options?: { fragmentId?: string | null }) => void;
  rtviClientRef: React.RefObject<PipecatClient | null>;
  currentSpeaker: 'local' | 'bot';
  mode: 'docked' | 'reader';
  onOpenReader: (slug: string) => void;
  onNavigateToBook: (slug: string) => void;
  readerBook: ReaderBook | null;
  isRenderProfileSupported: boolean;
  chapterRuntime: ChapterRuntime | null;
  chapterTimingsStatus: ChapterTimingsStatus;
  selectedReadingVoiceKey: string;
  isLoadingReaderBook: boolean;
  readerBookError: string | null;
  onRetryReaderBook: () => void;
  chapterRecoveryNotice: string | null;
  positionRequest: ReaderPositionRequest | null;
  pendingResumeSeconds: number | null;
  onReaderContentReadinessChange: (readiness: ReaderContentReadiness | null) => void;
  onReaderPositioned: (acknowledgement: ReaderPositionAcknowledgement | null) => void;
  onSeekAndPlay: (time: number, targetChapterId?: string) => void;
  onOpenTextChat?: (
    initialMessage?: string,
    forceOpen?: boolean,
    context?: InteractionContext,
  ) => void;
  isTextChatOpen?: boolean;
  textChatDraft?: string;
  conversationSessions?: readonly ChatSessionSummary[];
  activeConversationId?: number | null;
  onOpenConversation?: (session: ChatSessionSummary, inputMethod: AnalyticsInputMethod) => void;
  sleepTimer: SleepTimerState | null;
  sleepTimerRemainingSeconds: number | null;
  onSetCountdownSleepTimer: (minutes: number) => void;
  onSetEndOfChapterSleepTimer: () => void;
  onCancelSleepTimer: () => void;
  playbackRate: number;
  onPlaybackRateChange: (rate: number) => void;
  /**
   * Book-total listening position. Owned by useAudioManager rather than
   * derived here: this component does not re-render on currentTime, and the
   * element reads 0 until a queued resume applies the saved offset.
   */
  getBookProgressSeconds: () => number;
}

const PersistentAudioPlayer: React.FC<PersistentAudioPlayerProps> = ({
  book,
  audioRef,
  isPlaying,
  isReady,
  currentTime,
  duration,
  togglePlay,
  skipForward,
  skipBackward,
  onConnect,
  onDisconnect,
  isConnected,
  connectionStatus,
  isBuffering,
  onSeek,
  currentChapter,
  chapters,
  onChapterSelect,
  rtviClientRef,
  currentSpeaker,
  mode,
  onOpenReader,
  onNavigateToBook,
  readerBook,
  isRenderProfileSupported,
  chapterRuntime,
  chapterTimingsStatus,
  selectedReadingVoiceKey,
  isLoadingReaderBook,
  readerBookError,
  onRetryReaderBook,
  chapterRecoveryNotice,
  positionRequest,
  pendingResumeSeconds,
  onReaderContentReadinessChange,
  onReaderPositioned,
  onSeekAndPlay,
  onOpenTextChat,
  isTextChatOpen = false,
  textChatDraft,
  conversationSessions,
  activeConversationId,
  onOpenConversation,
  sleepTimer,
  sleepTimerRemainingSeconds,
  onSetCountdownSleepTimer,
  onSetEndOfChapterSleepTimer,
  onCancelSleepTimer,
  playbackRate,
  onPlaybackRateChange,
  getBookProgressSeconds,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [isMobileViewport, setIsMobileViewport] = useState(window.innerWidth < 768);
  const [isOpening, setIsOpening] = useState(true);
  const [showChapterList, setShowChapterList] = useState(false);
  const chapterSelectionRequestIdRef = useRef(0);
  const [chapterSelectionRequest, setChapterSelectionRequest] = useState<ReaderChapterSelectionRequest | null>(null);
  const [showSleepTimer, setShowSleepTimer] = useState(false);
  const [showPlaybackSpeed, setShowPlaybackSpeed] = useState(false);
  // Docked and reader layouts render different AlephEntry instances. Own the
  // selection above both so entering or leaving /read does not reset it.
  const [conversationMode, setConversationMode] = useState<ConversationMode>('voice');
  const chapterSelectorRef = useRef<HTMLDivElement>(null);
  const sleepTimerRef = useRef<HTMLDivElement>(null);
  const currentTimeRef = useRef<HTMLSpanElement>(null);
  const timeLeftRef = useRef<HTMLSpanElement>(null);
  const breakpoint = useBreakpoint();
  const usesMobileChatLayout = breakpoint !== 'md' && breakpoint !== 'lg' && breakpoint !== 'xl' && breakpoint !== '2xl';
  const isDockedChatOverlayOpen = mode === 'docked' && usesMobileChatLayout && isTextChatOpen;
  const hidesConversationBarForChat = mode === 'docked' && !usesMobileChatLayout && isTextChatOpen;
  const shouldLockPage = isDockedChatOverlayOpen;

  const sizeMap: Record<string, ButtonSize> = {
    base: "md",
    "3xs": "md",
    "2xs": "md",
    xs: "md",
    sm: "md",
    md: "md",
    lg: "md",
    xl: "md",
    "2xl": "md",
  };
  const controlSize = sizeMap[breakpoint];
  const showDockedChapterControls = breakpoint === 'xl' || breakpoint === '2xl';

  const chapterButtonSizes: Record<ButtonSize, string> = {
    base: 'h-12 w-12',
    sm: 'h-12 w-12',
    md: 'h-14 w-14',
    lg: 'h-16 w-16',
    xl: 'h-18 w-18',
  };

  const chapterIconSizes: Record<ButtonSize, 'sm' | 'md' | 'lg' | 'xl'> = {
    base: 'md',
    sm: 'md',
    md: 'lg',
    lg: 'lg',
    xl: 'xl',
  };

  const iconControlClassName = `flex aspect-square cursor-pointer items-center justify-center rounded-full transition-colors ${chapterButtonSizes[controlSize]} ${
    isDarkMode
      ? 'text-gray-200'
      : 'text-gray-800'
  }`;

  useClickOutside(chapterSelectorRef, () => setShowChapterList(false));
  useClickOutside(sleepTimerRef, () => setShowSleepTimer(false));

  useEffect(() => {
    if (!showDockedChapterControls || mode !== 'docked' || isDockedChatOverlayOpen) {
      setShowChapterList(false);
      setShowSleepTimer(false);
      setShowPlaybackSpeed(false);
    }
  }, [isDockedChatOverlayOpen, mode, showDockedChapterControls]);

  useEffect(() => {
    if (!showSleepTimer) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowSleepTimer(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSleepTimer]);

  useEffect(() => {
    // If mounting in full view, we don't need the opening animation for the docked player
    if (mode === 'reader') {
      setIsOpening(false);
      return;
    }

    // Trigger the animation after a brief delay to ensure initial render is complete
    const timer = setTimeout(() => {
      setIsOpening(false);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const checkWidth = () => {
      setIsMobileViewport(window.innerWidth < 768);
    };
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  const updateDockedTimeLabels = useCallback(() => {
    const audioTime = getDisplayedPlaybackTime(audioRef.current, currentTime, pendingResumeSeconds);
    if (currentTimeRef.current) {
      currentTimeRef.current.textContent = formatTime(audioTime);
    }
    if (timeLeftRef.current) {
      timeLeftRef.current.textContent = `-${formatTime(Math.max(0, duration - audioTime))}`;
    }
  }, [audioRef, currentTime, duration, pendingResumeSeconds]);

  useEffect(() => {
    const audio = audioRef.current;
    let intervalId: number | null = null;

    const stopInterval = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };

    const startInterval = () => {
      updateDockedTimeLabels();
      if (!audio || audio.paused || audio.ended) {
        stopInterval();
        return;
      }
      if (intervalId === null) {
        intervalId = window.setInterval(updateDockedTimeLabels, 250);
      }
    };

    updateDockedTimeLabels();
    startInterval();

    audio?.addEventListener('play', startInterval);
    audio?.addEventListener('playing', startInterval);
    audio?.addEventListener('pause', stopInterval);
    audio?.addEventListener('ended', stopInterval);
    audio?.addEventListener('seeked', updateDockedTimeLabels);
    audio?.addEventListener('timeupdate', updateDockedTimeLabels);
    audio?.addEventListener('loadedmetadata', updateDockedTimeLabels);

    return () => {
      stopInterval();
      audio?.removeEventListener('play', startInterval);
      audio?.removeEventListener('playing', startInterval);
      audio?.removeEventListener('pause', stopInterval);
      audio?.removeEventListener('ended', stopInterval);
      audio?.removeEventListener('seeked', updateDockedTimeLabels);
      audio?.removeEventListener('timeupdate', updateDockedTimeLabels);
      audio?.removeEventListener('loadedmetadata', updateDockedTimeLabels);
    };
  }, [audioRef, updateDockedTimeLabels]);

  const playerSurface = mode === 'reader' ? 'reader' : 'docked_player';

  const trackPlayerEvent = (eventName: string, extraProps = {}) => {
    bookAnalytics.trackPlayerEvent(eventName, {
      book,
      progressSeconds: getBookProgressSeconds(),
      isPlaying,
      isConnected,
      surface: playerSurface,
    }, extraProps);
  };

  // Applying a chapter change is shared with the reader; reporting it is not.
  // Each selector labels its own event — the reader's live in ReadPlayer, which
  // knows whether the pick came from its popup, its panel or an in-text link —
  // so the tracking deliberately does not travel down with this handler.
  const applyChapterSelection = (chapterId: string, options?: { fragmentId?: string | null }) => {
    if (mode === 'reader') {
      chapterSelectionRequestIdRef.current += 1;
      setChapterSelectionRequest({
        requestId: chapterSelectionRequestIdRef.current,
        chapterId,
        fragmentId: options?.fragmentId ?? null,
      });
    }
    onChapterSelect(chapterId, options);
  };

  const handleDockedChapterSelect = (chapterId: string) => {
    const toIndex = chapters.findIndex((c) => c.id === chapterId);
    trackPlayerEvent('Chapter Selected', {
      from_chapter_id: currentChapter?.id,
      to_chapter_id: chapterId,
      chapter_index: toIndex,
      total_chapters: chapters.length,
      chapter_selector_surface: 'docked',
      trigger: 'button',
      input_method: 'unknown',
    });
    applyChapterSelection(chapterId);
  };

  // No tracking here: app.tsx reports the reader open once the reader is live,
  // which covers every entry point rather than just this one.
  const handleOpenReader = () => {
    onOpenReader(book.slug);
  };

  const bind = useDrag(({ swipe: [, swipeY] }) => {
    if (swipeY === -1) {
      handleOpenReader();
    }
  }, {
    pointer: { touch: true },
    swipe: { distance: 20, velocity: 0.2 }
  });

  useLayoutEffect(() => {
    if (!shouldLockPage) return;

    const body = document.body;
    const root = document.documentElement;
    const lockedScrollX = window.scrollX;
    const lockedScrollY = window.scrollY;
    const previousBodyStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      paddingRight: body.style.paddingRight,
    };
    const previousRootStyles = {
      overflow: root.style.overflow,
      overflowY: root.style.overflowY,
      scrollbarGutter: root.style.scrollbarGutter,
      backgroundColor: root.style.backgroundColor,
    };

    body.style.position = 'fixed';
    body.style.top = `-${lockedScrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    root.style.overflowY = 'scroll';
    root.style.backgroundColor = isDarkMode
      ? 'var(--background-colour-dark-mode)'
      : 'var(--background-colour-light-mode)';

    return () => {
      body.style.position = previousBodyStyles.position;
      body.style.top = previousBodyStyles.top;
      body.style.left = previousBodyStyles.left;
      body.style.right = previousBodyStyles.right;
      body.style.width = previousBodyStyles.width;
      body.style.paddingRight = previousBodyStyles.paddingRight;
      root.style.overflow = previousRootStyles.overflow;
      root.style.overflowY = previousRootStyles.overflowY;
      root.style.scrollbarGutter = previousRootStyles.scrollbarGutter;
      root.style.backgroundColor = previousRootStyles.backgroundColor;
      window.scrollTo(lockedScrollX, lockedScrollY);
    };
  }, [isDarkMode, shouldLockPage]);

  const handlePlayPause = () => {
    // If connected, clicking should stop (disconnect)
    if (isConnected || isBuffering) {
      void onDisconnect({
        surface: playerSurface,
        end_reason: 'user_stop',
        trigger: 'button',
        input_method: 'unknown',
      });
      // Also pause the audio
      if (mode === 'docked' && audioRef.current && isPlaying) {
        audioRef.current.pause();
      }
    } else {
      // Otherwise, toggle play/pause normally
      togglePlay();
    }

    // Mixpanel track play/pause/stop button click
    const action = (isConnected || isBuffering) ? 'stop' : (isPlaying ? 'pause' : 'play');
    trackPlayerEvent('Audio Player Play/Pause/Stop Button', {
      action,
      trigger: 'button',
      input_method: 'unknown',
    });
  };

  const handleSkipBackward = () => {
    skipBackward();
    trackPlayerEvent('Player Skip', {
      direction: 'backward',
      seconds: 10,
      trigger: 'button',
      input_method: 'unknown',
    });
  };

  const handleSkipForward = () => {
    skipForward();
    trackPlayerEvent('Player Skip', {
      direction: 'forward',
      seconds: 10,
      trigger: 'button',
      input_method: 'unknown',
    });
  };

  const handleSkipBackwardKeyboard = () => {
    skipBackward();
    trackPlayerEvent('Player Skip', {
      direction: 'backward',
      seconds: 10,
      trigger: 'keyboard',
      input_method: 'keyboard',
    });
  };

  const handleSkipForwardKeyboard = () => {
    skipForward();
    trackPlayerEvent('Player Skip', {
      direction: 'forward',
      seconds: 10,
      trigger: 'keyboard',
      input_method: 'keyboard',
    });
  };

  usePlayerKeyboardShortcuts({
    togglePlay: handlePlayPause,
    skipForward: handleSkipForwardKeyboard,
    skipBackward: handleSkipBackwardKeyboard,
  });

  return (
    <>
      {mode === 'reader' ? <ReadPlayer
        book={book}
        isPlaying={isPlaying}
        isReady={isReady}
        currentTime={currentTime}
        duration={duration}
        onTogglePlay={handlePlayPause}
        onSkipForward={handleSkipForward}
        onSkipBackward={handleSkipBackward}
        getProgressSeconds={getBookProgressSeconds}
        onConnect={onConnect}
        onDisconnect={onDisconnect}
        isConnected={isConnected}
        connectionStatus={connectionStatus}
        isBuffering={isBuffering}
        onSeek={onSeek}
        currentChapter={currentChapter}
        chapters={chapters}
        onChapterSelect={applyChapterSelection}
        chapterSelectionRequest={chapterSelectionRequest}
        audioRef={audioRef}
        rtviClientRef={rtviClientRef}
        currentSpeaker={currentSpeaker}
        readerBook={readerBook}
        isRenderProfileSupported={isRenderProfileSupported}
        chapterRuntime={chapterRuntime}
        chapterTimingsStatus={chapterTimingsStatus}
        selectedReadingVoiceKey={selectedReadingVoiceKey}
        isLoadingReaderBook={isLoadingReaderBook}
        readerBookError={readerBookError}
        onRetryReaderBook={onRetryReaderBook}
        chapterRecoveryNotice={chapterRecoveryNotice}
        positionRequest={positionRequest}
        pendingResumeSeconds={pendingResumeSeconds}
        onReaderContentReadinessChange={onReaderContentReadinessChange}
        onReaderPositioned={onReaderPositioned}
        onSeekAndPlay={onSeekAndPlay}
        onOpenTextChat={onOpenTextChat}
        isTextChatOpen={isTextChatOpen}
        textChatDraft={textChatDraft}
        conversationSessions={conversationSessions}
        activeConversationId={activeConversationId}
        onOpenConversation={onOpenConversation}
        sleepTimer={sleepTimer}
        sleepTimerRemainingSeconds={sleepTimerRemainingSeconds}
        onSetCountdownSleepTimer={onSetCountdownSleepTimer}
        onSetEndOfChapterSleepTimer={onSetEndOfChapterSleepTimer}
        onCancelSleepTimer={onCancelSleepTimer}
        playbackRate={playbackRate}
        onPlaybackRateChange={onPlaybackRateChange}
        conversationMode={conversationMode}
        onConversationModeChange={setConversationMode}
      /> : (
      // Bottom padding tops the row's own 1.25rem (p-5) up to the home
      // indicator inset rather than adding the whole inset on top of it.
      <div
        className={`persistent-audio-player-container fixed right-0 bottom-0 left-0 h-auto w-full pb-[max(0px,calc(var(--safe-bottom)_-_1.25rem))] ${isDockedChatOverlayOpen ? 'hidden' : ''}`}
        style={{
          backgroundColor: isDarkMode ? 'var(--background-colour-dark-mode)' : 'var(--background-colour-light-mode)',
          touchAction: 'none',
          transform: isOpening ? 'translateY(100%)' : 'translateY(0)',
          transition: 'transform 150ms ease-in-out'
        }}
        onClick={handleOpenReader}
        {...bind()}
      >
        <div
          className="h-full w-full"
          style={{
            backgroundColor: isDarkMode ? 'var(--background-colour-dark-mode)' : 'var(--background-colour-light-mode)',
          }}
        >
          <div className="progress-container flex w-full flex-col items-center gap-0" onClick={(e) => e.stopPropagation()}>
            <div
              className={`flex w-full items-center gap-3 pl-[max(1.25rem,var(--safe-left))] pr-[max(1.25rem,var(--safe-right))] pb-4 text-sm tabular-nums select-none ${isDarkMode ? 'text-theme-primary-dark' : 'text-blue-500'}`}
            >
              <span ref={currentTimeRef} className="shrink-0">{formatTime(pendingResumeSeconds ?? currentTime)}</span>
              <ProgressBar
                duration={duration}
                currentTime={currentTime}
                onSeek={onSeek}
                isPlaying={isPlaying}
                audioRef={audioRef}
                className="rounded-full"
                imperativePlayback={true}
                pendingResumeSeconds={pendingResumeSeconds}
              />
              <span ref={timeLeftRef} className="shrink-0">-{formatTime(Math.max(0, duration - (pendingResumeSeconds ?? currentTime)))}</span>
            </div>
          </div>

          <div className="relative grid h-24 w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-4 p-5 pt-0 select-none cursor-default md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1.6fr)] md:gap-4">
              {/* Book Header */}
              <div
                className="col-start-1 row-start-1 flex w-full min-w-0 items-center gap-5 justify-self-start"
              >
                 <div className="h-18 w-18 shrink-0 cursor-pointer overflow-hidden rounded-sm">
                   <img
                      src={book.coverUrl}
                      alt={`${book.title} cover`}
                      className="h-full w-full object-cover" style={{ objectPosition: 'bottom' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onNavigateToBook(book.slug);
                      }}
                   />
                 </div>
                 {/* flex-1 stretches to the column edge so the tutorial spotlight
                     covers the tap-to-expand area without including the cover
                     (whose click navigates to the book page instead). */}
                 <div data-tutorial-id="player-expand" className={`flex-1 flex min-w-0 flex-col text-left`}>
                   <h3 className={`portrait-text-app line-clamp-2 font-regular text-xl ${isDarkMode ? 'text-theme-primary-dark' : 'text-gray-800'}`}>
                     <span>{book.title}</span>
                     {book.subheading && (
                       <span>:&nbsp;{book.subheading}</span>
                     )}
                   </h3>
                   <p className={`line-clamp-1 text-sm ${isDarkMode ? 'text-theme-secondary-dark' : 'text-black-700'}`}>by {book.author}</p>
                  </div>
              </div>

              <div className="col-start-2 row-start-1 flex items-center justify-end gap-5 md:contents">
                <div
                  className="flex shrink-0 items-center md:col-start-2 md:row-start-1 md:justify-self-center 2xl:absolute 2xl:col-auto 2xl:row-auto 2xl:left-1/2 2xl:top-[calc(50%_-_0.625rem)] 2xl:z-10 2xl:-translate-x-1/2 2xl:-translate-y-1/2 2xl:justify-self-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                    <PlayerControls
                      isPlaying={isPlaying}
                      isReady={isReady}
                      isBuffering={isBuffering}
                      isConnected={isConnected}
                      onTogglePlay={handlePlayPause}
                    onSkipBackward={handleSkipBackward}
                    onSkipForward={handleSkipForward}
                    size={controlSize}
                    plainPlayButton
                    playOnly={isMobileViewport}
                    leadingControl={
                      showDockedChapterControls ? (
                        <>
                        <div ref={chapterSelectorRef} className="relative shrink-0">
                          <button
                            type="button"
                            aria-label="Select chapter"
                            onClick={() => {
                              setShowSleepTimer(false);
                              setShowPlaybackSpeed(false);
                              setShowChapterList((open) => !open);
                            }}
                            className={iconControlClassName}
                          >
                            <ChapterSelectionIcon size={chapterIconSizes[controlSize]} />
                          </button>

                          <ControlPopupPresence isOpen={showChapterList}>
                            {(animationState) => (
                                <ChapterList
                                  chapters={chapters}
                                  currentChapterId={currentChapter?.id}
                                  animationClassName={`control-popup-native-${animationState}`}
                                  onChapterSelect={(chapterId) => {
                                    handleDockedChapterSelect(chapterId);
                                    setShowChapterList(false);
                                  }}
                                />
                            )}
                          </ControlPopupPresence>
                        </div>

                        <div ref={sleepTimerRef} className="relative shrink-0">
                          <button
                            type="button"
                            aria-label={getSleepTimerButtonLabel(sleepTimer, sleepTimerRemainingSeconds)}
                            aria-expanded={showSleepTimer}
                            aria-controls="docked-sleep-timer-menu"
                            aria-pressed={Boolean(sleepTimer)}
                            onClick={() => {
                              setShowChapterList(false);
                              setShowPlaybackSpeed(false);
                              setShowSleepTimer((open) => !open);
                            }}
                            className={`${iconControlClassName} relative`}
                          >
                            <ClockIcon size={chapterIconSizes[controlSize]} />
                            {sleepTimer && (
                              <span
                                className={`absolute top-2 right-2 h-2 w-2 rounded-full ${
                                  isDarkMode ? 'bg-white' : 'bg-black'
                                }`}
                                aria-hidden="true"
                              />
                            )}
                          </button>

                          <ControlPopupPresence isOpen={showSleepTimer}>
                            {(animationState) => (
                                <SleepTimerMenu
                                  id="docked-sleep-timer-menu"
                                  sleepTimer={sleepTimer}
                                  remainingSeconds={sleepTimerRemainingSeconds}
                                  animationClassName={`control-popup-native-${animationState}`}
                                  onSelectMinutes={(minutes) => {
                                    trackPlayerEvent('Sleep Timer Set', {
                                      mode: 'countdown',
                                      minutes,
                                      trigger: 'button',
                                      input_method: 'unknown',
                                    });
                                    onSetCountdownSleepTimer(minutes);
                                  }}
                                  onSelectEndOfChapter={() => {
                                    trackPlayerEvent('Sleep Timer Set', {
                                      mode: 'end_of_chapter',
                                      trigger: 'button',
                                      input_method: 'unknown',
                                    });
                                    onSetEndOfChapterSleepTimer();
                                  }}
                                  onCancel={() => {
                                    trackPlayerEvent('Sleep Timer Cancelled', {
                                      mode: sleepTimer?.mode,
                                      remaining_seconds: sleepTimerRemainingSeconds,
                                      trigger: 'button',
                                      input_method: 'unknown',
                                    });
                                    onCancelSleepTimer();
                                  }}
                                  onClose={() => setShowSleepTimer(false)}
                                />
                            )}
                          </ControlPopupPresence>
                        </div>
                        </>
                      ) : undefined
                    }
                    trailingControl={
                      showDockedChapterControls ? (
                        <>
                        <PlaybackSpeedControl
                          id="docked-playback-speed-menu"
                          size={controlSize}
                          playbackRate={playbackRate}
                          onPlaybackRateChange={onPlaybackRateChange}
                          isOpen={showPlaybackSpeed}
                          onToggle={() => {
                            setShowChapterList(false);
                            setShowSleepTimer(false);
                            setShowPlaybackSpeed((open) => !open);
                          }}
                          onClose={() => setShowPlaybackSpeed(false)}
                        />

                        <div className={`shrink-0 ${chapterButtonSizes[controlSize]}`} aria-hidden="true" />
                        </>
                      ) : undefined
                    }
                  />
                </div>

                <motion.div
                  className="flex min-w-0 items-center justify-end md:col-start-3 md:row-start-1 md:w-full md:justify-self-end"
                  initial={false}
                  animate={{
                    opacity: hidesConversationBarForChat ? 0 : 1,
                    x: hidesConversationBarForChat ? 24 : 0,
                    scale: hidesConversationBarForChat ? 0.98 : 1,
                  }}
                  transition={hidesConversationBarForChat
                    ? { duration: 0.12, ease: 'easeOut' }
                    : { duration: 0.18, delay: 0.12, ease: 'easeOut' }}
                  aria-hidden={hidesConversationBarForChat}
                  inert={hidesConversationBarForChat}
                  style={{ pointerEvents: hidesConversationBarForChat ? 'none' : 'auto' }}
                >
                  {/* Connect Button */}
                  <div className="order-last flex min-w-0 items-center justify-center md:w-full md:max-w-[26rem] 2xl:max-w-100" onClick={(e) => e.stopPropagation()}>
                      <AlephEntry
                          book={book}
                          getProgressSeconds={getBookProgressSeconds}
                          analyticsSurface="docked_player"
                          onConnect={async (context) => {
                            handleOpenReader();
                            return onConnect(context);
                          }}
                          onDisconnect={onDisconnect}
                          isConnected={isConnected}
                          connectionStatus={connectionStatus}
                          isBuffering={isBuffering}
                          onType={isMobileViewport ? undefined : (context) =>
                            onOpenTextChat?.(undefined, true, context)}
                          hideMobileTextComposer={isTextChatOpen && usesMobileChatLayout}
                          draftText={textChatDraft}
                          size={sizeMap[breakpoint]}
                          variant={isMobileViewport ? 'default' : 'expanded'}
                          compact={!isMobileViewport}
                          matchConversationSurface
                          mode={conversationMode}
                          onModeChange={setConversationMode}
                      />
                  </div>
                </motion.div>
              </div>
            </div>
        </div>
      </div>
      )}
    </>
  );
};

// Deliberately excludes currentTime, which is handled imperatively via
// refs/audioRef above, mirroring ReadPlayer's memo comparator.
// Navigation is compared because its callback captures view-history state.
const arePersistentAudioPlayerPropsEqual = (
  previous: PersistentAudioPlayerProps,
  next: PersistentAudioPlayerProps
): boolean => {
  return (
    previous.book === next.book &&
    previous.audioRef === next.audioRef &&
    previous.isPlaying === next.isPlaying &&
    previous.isReady === next.isReady &&
    previous.duration === next.duration &&
    previous.isConnected === next.isConnected &&
    previous.connectionStatus === next.connectionStatus &&
    previous.isBuffering === next.isBuffering &&
    previous.currentChapter === next.currentChapter &&
    previous.chapters === next.chapters &&
    previous.rtviClientRef === next.rtviClientRef &&
    previous.currentSpeaker === next.currentSpeaker &&
    previous.mode === next.mode &&
    previous.onOpenReader === next.onOpenReader &&
    previous.onNavigateToBook === next.onNavigateToBook &&
    previous.readerBook === next.readerBook &&
    previous.isRenderProfileSupported === next.isRenderProfileSupported &&
    previous.chapterRuntime === next.chapterRuntime &&
    previous.chapterTimingsStatus === next.chapterTimingsStatus &&
    previous.selectedReadingVoiceKey === next.selectedReadingVoiceKey &&
    previous.isLoadingReaderBook === next.isLoadingReaderBook &&
    previous.readerBookError === next.readerBookError &&
    previous.chapterRecoveryNotice === next.chapterRecoveryNotice &&
    previous.positionRequest === next.positionRequest &&
    previous.pendingResumeSeconds === next.pendingResumeSeconds &&
    previous.isTextChatOpen === next.isTextChatOpen &&
    previous.textChatDraft === next.textChatDraft &&
    previous.conversationSessions === next.conversationSessions &&
    previous.activeConversationId === next.activeConversationId &&
    previous.onOpenConversation === next.onOpenConversation &&
    previous.sleepTimer === next.sleepTimer &&
    previous.sleepTimerRemainingSeconds === next.sleepTimerRemainingSeconds &&
    previous.playbackRate === next.playbackRate &&
    previous.onPlaybackRateChange === next.onPlaybackRateChange
  );
};

export default React.memo(PersistentAudioPlayer, arePersistentAudioPlayerPropsEqual);
